import { useState, useEffect, useRef } from "react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  productName: string;
  productId: string;
}

type SubmitState = "idle" | "submitting" | "success" | "error";

export default function BackInStockModal({ isOpen, onClose, productName, productId }: Props) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setEmailError("");
      setSubmitState("idle");
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const validateEmail = (val: string) => {
    if (!val.trim()) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Please enter a valid email address.";
    return "";
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const err = validateEmail(email);
    if (err) {
      setEmailError(err);
      return;
    }
    setEmailError("");
    setSubmitState("submitting");

    try {
      const body = new URLSearchParams({
        email,
        product_name: productName,
        product_id: productId,
      });

      const res = await fetch("https://readdy.ai/api/form/d767rtvlpdqvlupfpqf0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitState("success");
      } else {
        setSubmitState("error");
      }
    } catch {
      setSubmitState("error");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="bis-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header band */}
        <div className="bg-gray-900 px-6 py-5 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-xl shrink-0">
              <i className="ri-notification-3-line text-white text-xl"></i>
            </div>
            <div>
              <h2 id="bis-title" className="text-white font-extrabold text-base leading-tight">
                Back in Stock Alert
              </h2>
              <p className="text-gray-400 text-xs mt-0.5">We&apos;ll email you the moment it&apos;s available</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer shrink-0 mt-0.5 rounded-lg hover:bg-white/10"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="px-6 py-6">
          {submitState === "success" ? (
            // ── Success state ──
            <div className="text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-4xl"></i>
              </div>
              <h3 className="text-gray-900 font-extrabold text-lg mb-2">You&apos;re on the list!</h3>
              <p className="text-gray-500 text-sm mb-1">
                We&apos;ll send a notification to <span className="font-semibold text-gray-800">{email}</span> as soon as
              </p>
              <p className="text-amber-600 font-semibold text-sm mb-6 leading-snug">{productName}</p>
              <p className="text-gray-400 text-xs mb-6">
                Typically restocks within 2–4 weeks. Check your inbox (and spam folder) for our email.
              </p>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Got it, thanks!
              </button>
            </div>
          ) : (
            // ── Form state ──
            <>
              {/* Product pill */}
              <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 mb-5">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 text-red-400">
                  <i className="ri-close-circle-fill"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-400 font-medium">Currently out of stock</p>
                  <p className="text-sm font-bold text-gray-800 truncate">{productName}</p>
                </div>
              </div>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="mb-4">
                  <label
                    htmlFor="bis-email"
                    className="block text-sm font-semibold text-gray-700 mb-1.5"
                  >
                    Your Email Address
                  </label>
                  <input
                    ref={inputRef}
                    id="bis-email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError(validateEmail(e.target.value));
                    }}
                    placeholder="you@example.com"
                    className={`w-full px-4 py-3 rounded-xl border text-sm text-gray-900 placeholder-gray-400 outline-none transition-colors ${
                      emailError
                        ? "border-red-400 bg-red-50 focus:border-red-500"
                        : "border-gray-200 bg-white focus:border-amber-400"
                    }`}
                  />
                  {emailError && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i> {emailError}
                    </p>
                  )}
                </div>

                {/* Hidden fields */}
                <input type="hidden" name="product_name" value={productName} />
                <input type="hidden" name="product_id" value={productId} />

                {/* Error banner */}
                {submitState === "error" && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0 text-red-500">
                      <i className="ri-error-warning-line"></i>
                    </div>
                    <p className="text-xs text-red-600">
                      Something went wrong. Please try again or call us at (866) 605-4975.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitState === "submitting"}
                  className="w-full py-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white font-bold text-sm transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  {submitState === "submitting" ? (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center animate-spin">
                        <i className="ri-loader-4-line"></i>
                      </div>
                      Sending…
                    </>
                  ) : (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-mail-send-line"></i>
                      </div>
                      Notify Me When Available
                    </>
                  )}
                </button>
              </form>

              <p className="text-center text-xs text-gray-400 mt-4 leading-relaxed">
                <i className="ri-lock-line mr-1 text-gray-300"></i>
                No spam, ever. We only contact you about this product&apos;s restock. Unsubscribe any time.
              </p>

              {/* Alt CTA */}
              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">Need it sooner?</p>
                <a
                  href="tel:8666054975"
                  className="flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </div>
                  Call (866) 605-4975
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
